import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type ProhibitionPreview, type ProhibitionRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

/**
 * The register of medicines ChiCare will not sell.
 *
 * ── HOW THIS DIFFERS FROM A RECALL ──────────────────────────────────────────
 *
 * A recall is about a BATCH: one bad lot, pulled from shelves, chased to the
 * people holding it. The medicine itself is fine and the next batch sells
 * normally. A ban is about the MEDICINE: no batch is sellable, no new listing
 * may be created, and it stands until somebody deliberately lifts it.
 *
 * They decay differently, which is why they are separate screens. A recall
 * closes when every shelf is confirmed clear. A ban does not close — and one
 * that quietly expired would start selling a withdrawn medicine by accident.
 *
 * ── WHY EVERY ENTRY NAMES AN AUTHORITY ──────────────────────────────────────
 *
 * Banning a medicine is a regulatory act, and the question that follows is
 * always the same: who said so, when, and on what. A boolean on a product
 * answers none of it. So the form asks for the body, their public reference,
 * and the regulator's own words — which is what a pharmacist reads before
 * pulling stock off a shelf, and what a lawyer asks for first.
 */
export function ProhibitionsScreen() {
  const [issuing, setIssuing] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'prohibitions'],
    queryFn: ({ signal }) => api.prohibitions(undefined, signal),
  });

  if (isError) {
    return (
      <AdminError title="We couldn't load the register" error={error} onRetry={() => refetch()} />
    );
  }

  if (issuing) {
    return <IssueBan onDone={() => setIssuing(false)} />;
  }

  const bans = data?.bans ?? [];

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-6 xl:px-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">
            Withdrawn medicines
          </h1>
          <p className="m-0 mt-1 max-w-[62ch] text-[13px] leading-relaxed text-muted">
            Medicines ChiCare will not sell. Taking one off here removes it from search, every
            category, the prescription board and the cart at once, and seizes it from every
            pharmacy holding it. It stands until somebody lifts it.
          </p>
        </div>

        <Button onClick={() => setIssuing(true)}>Withdraw a medicine</Button>
      </header>

      {isLoading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-[66px] w-full" radius="card" />
          ))}
        </div>
      ) : bans.length === 0 ? (
        <EmptyState
          title="Nothing is withdrawn"
          body="When NAFDAC bans a molecule or withdraws a registration, this is where it comes off the platform — from every pharmacy at once."
          action={<Button onClick={() => setIssuing(true)}>Withdraw a medicine</Button>}
        />
      ) : (
        <div className="overflow-hidden rounded-card border border-line bg-white">
          {bans.map((ban) => (
            <BanRow key={ban.reference} ban={ban} />
          ))}
        </div>
      )}
    </div>
  );
}

function BanRow({ ban }: { ban: ProhibitionRow }) {
  const queryClient = useQueryClient();
  const [lifting, setLifting] = useState(false);
  const [reason, setReason] = useState('');

  const lift = useMutation({
    mutationFn: () => api.liftProhibition(ban.reference, reason.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prohibitions'] });
      setLifting(false);
      setReason('');
    },
  });

  const error = lift.error instanceof ApiError ? lift.error : null;

  return (
    <div
      data-testid={`ban-${ban.reference}`}
      className={cn(
        'border-b border-line px-4 py-3 last:border-b-0',
        ban.in_force ? 'bg-white' : 'bg-paper',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1 basis-[240px]">
          <p className="m-0 flex flex-wrap items-center gap-2">
            <span
              className={cn('text-[14px] font-bold', ban.in_force ? 'text-ink' : 'text-muted')}
            >
              {ban.name}
            </span>
            {ban.in_force ? (
              <span className="rounded-pill bg-danger-tint px-2 py-[2px] text-[10.5px] font-bold text-danger">
                Withdrawn
              </span>
            ) : (
              <span className="rounded-pill bg-paper px-2 py-[2px] text-[10.5px] font-medium text-muted">
                Lifted
              </span>
            )}
          </p>

          <p className="m-0 mt-0.5 font-mono text-[11px] text-faint">
            {ban.authority}
            {ban.reference_no ? ` · ${ban.reference_no}` : ''} · {ban.reference}
          </p>

          <p className="m-0 mt-1.5 max-w-[70ch] text-[12.5px] leading-snug text-muted">
            {ban.reason}
          </p>
        </div>

        {/* What it actually took off the platform. Counted, never zeroed — the
            packs are unsellable, not imaginary, and an inspector wants to know
            how many are sitting there. */}
        <div className="shrink-0 text-right">
          <p className="m-0 font-mono text-[13px] font-bold tabular-nums text-ink">
            {ban.listings_seized}
          </p>
          <p className="m-0 text-[11px] text-faint">
            listing{ban.listings_seized === 1 ? '' : 's'} seized
          </p>
          <p className="m-0 mt-1 font-mono text-[12px] tabular-nums text-muted">
            {ban.units_removed} packs
          </p>
        </div>
      </div>

      {ban.in_force ? (
        lifting ? (
          <div className="mt-3 border-t border-line pt-3">
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-ink">
                What changed? A ban lifted without a reason is one nobody can defend.
              </span>
              <textarea
                value={reason}
                rows={2}
                data-testid={`lift-reason-${ban.reference}`}
                onChange={(event) => setReason(event.target.value)}
                placeholder="NAFDAC reinstated the registration after the manufacturer corrected the fault."
                className="w-full rounded-inp border border-line bg-white px-3 py-2 text-[13px] leading-relaxed text-ink outline-none focus:border-blue"
              />
            </label>

            {error ? (
              <p role="alert" className="m-0 mt-1.5 text-[12px] font-medium text-danger">
                {error.fieldErrors.reason?.[0] ?? error.message}
              </p>
            ) : null}

            <p className="m-0 mt-1.5 text-[11.5px] leading-snug text-faint">
              This puts the medicine back in the catalogue. It does NOT put the listings back —
              each pharmacy decides whether it still wants to stock it.
            </p>

            <div className="mt-2.5 flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={reason.trim().length < 20 || lift.isPending}
                onClick={() => lift.mutate()}
              >
                {lift.isPending ? 'Lifting…' : 'Lift the ban'}
              </Button>
              <Button size="sm" variant="grey" onClick={() => setLifting(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setLifting(true)}
            className="mt-2 min-h-11 bg-transparent px-0 text-[12.5px] font-semibold text-muted hover:text-blue"
          >
            Lift this ban
          </button>
        )
      ) : ban.lifted_reason ? (
        <p className="m-0 mt-2 border-t border-line pt-2 text-[12px] leading-snug text-muted">
          <b className="text-ink">Lifted:</b> {ban.lifted_reason}
        </p>
      ) : null}
    </div>
  );
}

// ── Issuing one ─────────────────────────────────────────────────────────────

function IssueBan({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();

  const [productId, setProductId] = useState('');
  const [name, setName] = useState('');
  const [nafdac, setNafdac] = useState('');
  const [sector, setSector] = useState('human');
  const [authority, setAuthority] = useState('NAFDAC');
  const [referenceNo, setReferenceNo] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<ProhibitionPreview | null>(null);

  const look = useMutation({
    mutationFn: () => api.previewProhibition(Number(productId)),
    onSuccess: (result) => {
      setPreview(result);

      // The regulator names the medicine their way, but if it is one we carry
      // the catalogue name saves somebody retyping it.
      if (result.product && name.trim() === '') {
        setName(result.product);
      }
    },
  });

  const issue = useMutation({
    mutationFn: () =>
      api.issueProhibition({
        product_id: productId ? Number(productId) : null,
        name: name.trim(),
        nafdac_reg_no: nafdac.trim() || undefined,
        sector,
        reason: reason.trim(),
        authority: authority.trim(),
        reference_no: referenceNo.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prohibitions'] });
      onDone();
    },
  });

  const error = [look.error, issue.error].find((one): one is ApiError => one instanceof ApiError);

  return (
    <div className="mx-auto w-full max-w-[620px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="mb-4 inline-flex min-h-11 items-center gap-1.5 bg-transparent p-0 text-[13px] font-semibold text-muted"
      >
        <Icon name="back" size={15} strokeWidth={2} />
        The register
      </button>

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
        Withdraw a medicine
      </h1>
      <p className="m-0 mt-1 text-[13px] leading-relaxed text-muted">
        This takes it off every shelf on the platform and stops it being listed again. It stands
        until somebody lifts it — unlike a recall, which ends when shelves are clear.
      </p>

      <div className="mt-4 space-y-3 rounded-card border border-line bg-white p-4 sm:p-5">
        <Input
          label="Product ID (if we carry it)"
          mono
          value={productId}
          placeholder="1482"
          hint="From the catalogue screen. Leave empty to ban something we have never listed — the name still goes on the register."
          error={error?.fieldErrors.product_id?.[0] ?? null}
          onChange={(event) => {
            setProductId(event.target.value);
            setPreview(null);
          }}
        />

        {productId ? (
          <Button
            size="sm"
            variant="grey"
            disabled={look.isPending}
            onClick={() => look.mutate()}
          >
            {look.isPending ? 'Checking…' : 'Check what this seizes'}
          </Button>
        ) : null}
      </div>

      {preview ? (
        <div
          data-testid="ban-preview"
          className="mt-3 rounded-card border border-caution bg-caution-tint px-4 py-3.5"
        >
          <p className="m-0 text-[13px] font-bold text-caution-ink">
            This seizes {preview.listings} listing{preview.listings === 1 ? '' : 's'} across{' '}
            {preview.pharmacies.length} pharmac{preview.pharmacies.length === 1 ? 'y' : 'ies'}.
          </p>
          <p className="m-0 mt-1 text-[12.5px] leading-snug text-caution-ink">
            {preview.units_on_shelves} pack{preview.units_on_shelves === 1 ? '' : 's'} of{' '}
            <b>{preview.product}</b> come off sale. They stay on the books — unsellable, not
            written off — because an inspector wants to know how many are sitting there.
          </p>
          {preview.pharmacies.length > 0 ? (
            <ul className="m-0 mt-2 list-none space-y-0.5 p-0">
              {preview.pharmacies.map((one) => (
                <li key={one} className="font-mono text-[11.5px] text-caution-ink">
                  {one}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="mt-4 space-y-3 rounded-card border border-line bg-white p-4 sm:p-5">
        <Input
          label="What is it called?"
          value={name}
          placeholder="Codeine-containing cough syrup"
          hint="The regulator's wording. It is what somebody searching the register will type."
          error={error?.fieldErrors.name?.[0] ?? null}
          onChange={(event) => setName(event.target.value)}
        />

        <Input
          label="NAFDAC number (optional)"
          mono
          value={nafdac}
          placeholder="A4-1234"
          onChange={(event) => setNafdac(event.target.value)}
        />

        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">Which shop</span>
          <select
            value={sector}
            data-testid="ban-sector"
            onChange={(event) => setSector(event.target.value)}
            className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
          >
            <option value="human">Pharmacy</option>
            <option value="veterinary">Veterinary</option>
          </select>
        </label>

        <Input
          label="Who ordered it?"
          value={authority}
          placeholder="NAFDAC"
          hint="NAFDAC, PCN, VCN, a court. The first thing anybody cross-checks."
          error={error?.fieldErrors.authority?.[0] ?? null}
          onChange={(event) => setAuthority(event.target.value)}
        />

        <Input
          label="Their reference (optional)"
          value={referenceNo}
          placeholder="2026/114"
          onChange={(event) => setReferenceNo(event.target.value)}
        />

        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">
            What did they say?
          </span>
          <textarea
            value={reason}
            rows={3}
            data-testid="ban-reason"
            onChange={(event) => setReason(event.target.value)}
            placeholder="NAFDAC has withdrawn the registration after contamination was found at the manufacturer."
            className="w-full rounded-inp border border-line bg-white px-3 py-2 text-[13.5px] leading-relaxed text-ink outline-none focus:border-blue"
          />
          <span className="mt-1 block text-[11.5px] leading-snug text-faint">
            A pharmacist reads this before pulling stock off a shelf. Write it for them.
          </span>
        </label>
      </div>

      {error ? (
        <p
          role="alert"
          className="m-0 mt-3 rounded-card border border-danger bg-danger-tint px-3.5 py-2.5 text-[12.5px] font-medium text-danger"
        >
          {error.fieldErrors.reason?.[0] ?? error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-2">
        <Button
          disabled={
            name.trim() === '' ||
            authority.trim() === '' ||
            reason.trim().length < 20 ||
            issue.isPending
          }
          onClick={() => issue.mutate()}
        >
          {issue.isPending ? 'Withdrawing…' : 'Withdraw it from sale'}
        </Button>

        <Button variant="grey" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
